/**
 * SUSTAINED-vs-GUST wind dynamics for the tower sim.
 *
 * The solver's `params.windSpeed` is a single "wind dial" number that TIA-222
 * treats as a **3-second peak gust** (see wind.ts / ENGINEERING.md §1). Real
 * wind is not one number: it is a slowly-varying **mean** (the "sustained" wind,
 * a 10-min or 1-min average) plus a fast, turbulent **fluctuating** component
 * whose short-duration peaks are the gusts. This module turns a chosen wind
 * setting into a *time-varying* instantaneous speed the solver can feed into
 * `velocityPressure()` each step, so we can offer a calm "steady" mode and a
 * punishing "gusty" mode from the same dial.
 *
 * Everything is SI (m/s, s, Hz). Pure + deterministic: no Math.random(), no
 * argless Date — the caller passes `t` (sim seconds) and a `seed`, so the same
 * inputs always reproduce the same gust trace (needed for replays / fairness).
 *
 * KEY FACTS & FACTORS (sources in docs/WIND_MODEL.md):
 *  - Durst (1960): the 3-s gust factor over the **hourly mean** in open terrain
 *    at 10 m is G ≈ **1.52**, which corresponds to a longitudinal turbulence
 *    intensity Iu ≈ **0.165 (16.5%)**. G ≈ 1 + g·Iu with a peak factor g ≈ 3.
 *  - Gust factor vs mean averaging window: ~1.05 (1-min), ~1.2–1.3 (short mean),
 *    up to ~1.5+ against the hourly mean. Rougher terrain ⇒ higher Iu ⇒ higher G.
 *  - Iu falls with height (Kz rises): ~0.15–0.20 near ground, ~0.10 up high in
 *    open terrain — steadier but higher-pressure aloft.
 *  - ASCE-7 / TIA-222 gust-effect factor Gf: a structure is **rigid** if its
 *    fundamental frequency f1 ≥ 1 Hz (Gf ≈ 0.85, no resonant magnification) and
 *    **flexible** if f1 < 1 Hz, where resonance with the gust energy pushes the
 *    dynamic-amplification/Gf above 1.0.
 */

/** Longitudinal turbulence intensity for Durst's classic 3-s gust factor. */
export const DURST_IU = 0.165;
/** Peak factor g in G = 1 + g·Iu (≈3 for a ~3-s peak; ASCE uses ~3.4 hourly). */
export const PEAK_FACTOR = 3.0;
/** Durst 3-s-gust-over-hourly-mean factor in open terrain at 10 m: 1 + 3·0.165. */
export const DURST_GUST_FACTOR = 1 + PEAK_FACTOR * DURST_IU; // ≈ 1.495 ≈ 1.5
/** ASCE-7 / TIA-222 rigid|flexible split: resonance matters below this f1 (Hz). */
export const FLEXIBLE_FREQ_HZ = 1.0;
/** Structural damping ratio ζ for bolted steel lattice/mast (~1–2% critical). */
export const STEEL_DAMPING = 0.015;

/**
 * A wind setting the game can hand to the solver.
 *
 * `meanSpeed` is the SUSTAINED wind (the mean the dial represents). `gustiness`
 * is the turbulence intensity Iu (0 = perfectly steady, ~0.165 = open-terrain
 * default, up to ~0.3 in rough/suburban terrain). A `WindState` with
 * gustiness = 0 reproduces a plain constant wind, so existing callers are safe.
 */
export interface WindState {
  /** Sustained (mean) wind speed, m/s. */
  meanSpeed: number;
  /** Turbulence intensity Iu, 0..~0.3 (0 = steady, 0.165 = Durst default). */
  gustiness: number;
  /** RNG seed so the gust trace is deterministic per game/round. */
  seed: number;
}

/** A steady wind: the dial value with no fluctuation (constant, safe default). */
export function steadyWind(meanSpeed: number, seed = 1): WindState {
  return { meanSpeed, gustiness: 0, seed };
}

/** A gusty wind at the open-terrain Durst turbulence intensity. */
export function gustyWind(meanSpeed: number, seed = 1, gustiness = DURST_IU): WindState {
  return { meanSpeed, gustiness, seed };
}

/**
 * Gust factor G = peak-gust / mean for a given turbulence intensity.
 * G = 1 + g·Iu (Durst / ASCE form). Defaults to the open-terrain Iu so
 * `gustFactor()` alone returns the canonical ~1.5.
 */
export function gustFactor(gustiness = DURST_IU): number {
  return 1 + PEAK_FACTOR * gustiness;
}

/** Peak (3-s) gust speed from a mean, given a gust factor. */
export function peakGust(mean: number, factor = DURST_GUST_FACTOR): number {
  return mean * factor;
}

/** Peak gust implied by a full WindState (mean × its gust factor). */
export function statePeakGust(state: WindState): number {
  return peakGust(state.meanSpeed, gustFactor(state.gustiness));
}

/**
 * Deterministic smooth pseudo-noise in [-1, 1]. Value-noise: hash the two
 * integer lattice points bracketing `x` to [-1,1] and smootherstep-interpolate,
 * so the output is C1-continuous (no velocity discontinuities that would jolt
 * the solver) yet reproducible from `(x, seed)`. No Math.random() involved.
 */
export function smoothNoise(x: number, seed: number): number {
  const i0 = Math.floor(x);
  const f = x - i0;
  const n0 = hash01(i0, seed) * 2 - 1;
  const n1 = hash01(i0 + 1, seed) * 2 - 1;
  // smootherstep 6t^5-15t^4+10t^3
  const u = f * f * f * (f * (f * 6 - 15) + 10);
  return n0 + (n1 - n0) * u;
}

/** Integer hash → [0,1). Deterministic; pure integer/bit ops, no RNG. */
function hash01(n: number, seed: number): number {
  let h = (n | 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (seed | 0), 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Timescales (Hz) of the gust content we synthesize — a small spectrum. */
const GUST_OCTAVES: ReadonlyArray<{ freq: number; weight: number }> = [
  { freq: 0.06, weight: 0.55 }, // slow lulls/surges (~16 s), carries most energy
  { freq: 0.2, weight: 0.3 }, // ~5 s swells
  { freq: 0.6, weight: 0.15 }, // ~1.6 s sharp gust fronts (near the 3-s peak band)
];

/**
 * Instantaneous wind speed at time `t` for a wind setting — the core call the
 * solver uses to modulate `params.windSpeed`.
 *
 * Model: V(t) = mean · (1 + Iu · turb(t)), where turb(t) is unit-ish smooth
 * multi-octave noise. The fluctuation scales with Iu (gustiness), so:
 *   - gustiness = 0  ⇒ V(t) = mean exactly  (STEADY mode, matches old behaviour)
 *   - gustiness > 0  ⇒ mean with lulls & gusts, peaks reaching ~mean·(1+g·Iu),
 *                      i.e. toward peakGust() during sharp fronts (GUSTY mode).
 * Clamped ≥ 0 so a deep lull can't produce a negative (reversed) wind speed.
 */
export function gustSpeed(state: WindState, t: number): number {
  if (state.gustiness <= 0) return state.meanSpeed; // steady: constant dial value
  let turb = 0;
  for (let k = 0; k < GUST_OCTAVES.length; k++) {
    const oct = GUST_OCTAVES[k]!;
    // offset each octave's phase by the seed+octave so they don't align
    turb += oct.weight * smoothNoise(t * oct.freq + state.seed * 0.37 + k * 11.1, state.seed + k);
  }
  // PEAK_FACTOR maps unit noise so that rare aligned peaks approach 1+g·Iu.
  const inst = state.meanSpeed * (1 + state.gustiness * PEAK_FACTOR * turb);
  return Math.max(0, inst);
}

/**
 * Dynamic amplification factor (DAF) for a single-DOF structure of fundamental
 * period `T1` (s) excited by gusts of characteristic period `Tgust` (s, ~3 s
 * for the design gust), with damping ratio `zeta`.
 *
 * Steady-state SDOF magnification: 1 / sqrt((1-r²)² + (2ζr)²), r = f_excite/f1.
 * - Rigid tower (T1 << Tgust, r→0): DAF→1  → the structure just follows the
 *   quasi-static gust load; the gust factor alone captures the loading.
 * - Flexible tower (f1 < 1 Hz, r→1): resonance blows DAF up to ~1/(2ζ)
 *   (≈30 at ζ=1.5%) — this is why tall guyed masts, not stubby towers, are the
 *   ones dynamic gust energy shakes apart.
 * Result is clamped to a sane game ceiling so near-resonance can't NaN/explode.
 */
export function dynamicAmplification(
  T1: number,
  Tgust = 3.0,
  zeta = STEEL_DAMPING,
): number {
  if (T1 <= 0 || Tgust <= 0) return 1;
  const r = Tgust / T1; // frequency ratio f_excite/f1 = (1/Tgust)/(1/T1) = T1/Tgust
  const ratio = 1 / r; // = f_excite / f1
  const denom = Math.sqrt(
    (1 - ratio * ratio) * (1 - ratio * ratio) + (2 * zeta * ratio) * (2 * zeta * ratio),
  );
  const daf = denom > 1e-6 ? 1 / denom : 1 / (2 * zeta);
  return Math.min(daf, 1 / (2 * zeta)); // cap at the resonant peak 1/(2ζ)
}

/** Is a structure "flexible" (f1 < 1 Hz ⇒ gust resonance matters)? */
export function isFlexible(f1Hz: number): boolean {
  return f1Hz < FLEXIBLE_FREQ_HZ;
}

/**
 * Rough fundamental frequency estimate for a tower/mast of height H (m), from
 * the classic code approximation f1 ≈ 1/T1 with T1 ≈ 0.019·H^0.75 (ASCE-7 steel
 * moment-frame period form; a serviceable stand-in for lattice/mast towers).
 * Lets the game key dynamicAmplification() off geometry when a modal solve
 * isn't available. Taller ⇒ lower f1 ⇒ more likely flexible ⇒ more resonance.
 */
export function estimateFundamentalHz(heightM: number): number {
  if (heightM <= 0) return FLEXIBLE_FREQ_HZ;
  const T1 = 0.019 * Math.pow(heightM, 0.75);
  return 1 / T1;
}
